import { useEffect } from 'react';
import { Box, Stack, Text } from 'braid-design-system';

export interface ToastProps {
  id: string;
  type: 'error' | 'warning' | 'success' | 'info';
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

export const Toast = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  onClose,
}: ToastProps) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  const getToastStyles = () => {
    const baseStyles = {
      position: 'fixed' as const,
      top: '150px',
      right: '20px',
      zIndex: 9999,
      minWidth: '300px',
      maxWidth: '400px',
      padding: '16px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      transform: 'translateX(0)',
      transition: 'all 0.3s ease-in-out',
    };

    const typeStyles = {
      error: {
        backgroundColor: '#fee',
        borderLeft: '4px solid #dc2626',
        color: '#991b1b',
      },
      warning: {
        backgroundColor: '#fffbeb',
        borderLeft: '4px solid #f59e0b',
        color: '#92400e',
      },
      success: {
        backgroundColor: '#f0fdf4',
        borderLeft: '4px solid #10b981',
        color: '#065f46',
      },
      info: {
        backgroundColor: '#eff6ff',
        borderLeft: '4px solid #3b82f6',
        color: '#1e40af',
      },
    };

    return { ...baseStyles, ...typeStyles[type] };
  };

  const getIcon = () => {
    switch (type) {
      case 'error':
        return '🚨';
      case 'warning':
        return '⚠️';
      case 'success':
        return '✅';
      case 'info':
        return 'ℹ️';
      default:
        return '📢';
    }
  };

  return (
    <Box style={getToastStyles()}>
      <Stack space="small">
        <Box
          display="flex"
          justifyContent="spaceBetween"
          alignItems="center"
        >
          <Box display="flex" alignItems="center">
            <Box paddingRight="small">
              <Text size="small">{getIcon()}</Text>
            </Box>
            <Stack space="xsmall">
              <Text weight="strong" size="small">
                {title}
              </Text>
              {message ? (
                <Text size="small" tone="secondary">
                  {message}
                </Text>
              ) : null}
            </Stack>
          </Box>
          <Box display="flex" alignItems="center">
            <button
              onClick={() => onClose(id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: 'inherit',
                padding: '4px',
              }}
            >
              ✕
            </button>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};
